// Pure derivation for the desktop Review pages (Slice 4). NOTHING here is persisted —
// every figure is recomputed from the raw endpoint response each render. The ONLY flag
// source is flagFor; the ONLY pct source is pct (both from ./recording).
import { flagFor, pct, type Flag } from './recording';
import { normalizeMeasurementType, typeDefaultThreshold, TYPE_COLOR_KEY,
         type SpecMeasurementType } from './pestConfig';

export interface RawPest {
  pestId: string; code: string; name: string;
  measurementType: string;          // stored term ('percentage' | 'level' | 'count')
  actionThreshold: number;
  samplingUnit: string; tallyThreshold: string; category: string; order: number;
}
export interface RawBlock {
  blockId: string; orchardId: string | null; orchardName: string; variety: string;
  date: string; scouterName: string; examinedCount: number; totalInspectionPoints: number;
  incidence: { pestId: string; infestedPoints: number }[];
  level: { pestId: string; valueSeverity: number }[];
  count: { pestId: string; valueCount: number }[];
  photos?: { photoUrl: string; inspectionPointId: number }[];
}

export interface PestCell {
  pestId: string; code: string; name: string;
  type: SpecMeasurementType; colorKey: 'inc' | 'lev' | 'cnt';
  inProtocol: boolean;
  value: number; threshold: number; display: string;
  flag: Flag | null;                // null when !inProtocol
}
export interface BlockReview {
  blockId: string; orchardId: string | null; orchardName: string; variety: string;
  date: string; scouterName: string; examinedCount: number; totalInspectionPoints: number;
  coveragePct: number;
  cells: PestCell[];
  counts: { exceeded: number; approaching: number; below: number };
  worst: { code: string; display: string; flag: Flag } | null;
  worstRatio: number;               // value/max(threshold,1) of the worst in-protocol pest; -1 when none (sort key)
  photos: { photoUrl: string; inspectionPointId: number }[];
}
export interface ReviewKpis {
  blocksOverThreshold: number; totalBlocks: number;
  pestBlockExceedances: number; approaching: number; avgCoveragePct: number;
}

const ORDINAL = ['–', 'x', 'xx', 'xxx', 'xxxx'];

function displayFor(type: SpecMeasurementType, value: number): string {
  if (type === 'incidence') return `${value}%`;
  if (type === 'level') return ORDINAL[Math.max(0, Math.min(4, value))] ?? `${value}`;
  return `${value}`;
}

export function thresholdFor(p: RawPest, type: SpecMeasurementType): number {
  return typeof p.actionThreshold === 'number' ? p.actionThreshold : typeDefaultThreshold(type);
}

/** Meter math (Impl §5.3): scale 0..threshold*1.25, mark at 80%, fill = min(100, value/scale*100). */
export function meterGeometry(value: number, threshold: number): { fillPct: number; markLeftPct: number } {
  const scale = threshold > 0 ? threshold * 1.25 : (value || 1);
  return { fillPct: Math.min(100, (value / scale) * 100), markLeftPct: 80 };
}

export function buildBlockReviews(blocks: RawBlock[], pests: RawPest[]): BlockReview[] {
  return blocks.map(b => {
    const incMap = new Map(b.incidence.map(r => [r.pestId, r.infestedPoints]));
    const lvlMap = new Map(b.level.map(r => [r.pestId, r.valueSeverity]));
    const cntMap = new Map(b.count.map(r => [r.pestId, r.valueCount]));

    const cells: PestCell[] = pests.map(p => {
      const type = normalizeMeasurementType(p.measurementType);
      const threshold = thresholdFor(p, type);
      let inProtocol = false;
      let value = 0;
      if (type === 'incidence') {
        inProtocol = incMap.has(p.pestId);
        value = inProtocol ? pct(incMap.get(p.pestId)!, b.examinedCount) : 0;
      } else if (type === 'level') {
        inProtocol = lvlMap.has(p.pestId);
        value = inProtocol ? lvlMap.get(p.pestId)! : 0;
      } else {
        inProtocol = cntMap.has(p.pestId);
        value = inProtocol ? cntMap.get(p.pestId)! : 0;
      }
      return {
        pestId: p.pestId, code: p.code, name: p.name, type,
        colorKey: TYPE_COLOR_KEY[type], inProtocol,
        value, threshold, display: displayFor(type, value),
        flag: inProtocol ? flagFor(value, threshold) : null,
      };
    });

    const counts = { exceeded: 0, approaching: 0, below: 0 };
    for (const c of cells) if (c.flag) counts[c.flag] += 1;

    let worst: BlockReview['worst'] = null;
    let worstRatio = -1;
    for (const c of cells) {
      if (!c.inProtocol || !c.flag) continue;
      const ratio = c.value / Math.max(c.threshold, 1);
      if (ratio > worstRatio) {
        worstRatio = ratio; worst = { code: c.code, display: c.display, flag: c.flag };
      }
    }

    const total = b.totalInspectionPoints || 100;
    return {
      blockId: b.blockId, orchardId: b.orchardId, orchardName: b.orchardName, variety: b.variety,
      date: b.date, scouterName: b.scouterName,
      examinedCount: b.examinedCount, totalInspectionPoints: total,
      coveragePct: Math.round((b.examinedCount / total) * 100),
      cells, counts, worst, worstRatio,
      photos: b.photos || [],
    };
  });
}

export function computeKpis(blocks: BlockReview[]): ReviewKpis {
  const blocksOver = blocks.filter(b => b.counts.exceeded > 0).length;
  const exceedances = blocks.reduce((s, b) => s + b.counts.exceeded, 0);
  const approaching = blocks.reduce((s, b) => s + b.counts.approaching, 0);
  const avg = blocks.length ? Math.round(blocks.reduce((s, b) => s + b.coveragePct, 0) / blocks.length) : 0;
  return { blocksOverThreshold: blocksOver, totalBlocks: blocks.length,
           pestBlockExceedances: exceedances, approaching, avgCoveragePct: avg };
}

export function kpiDeltas(cur: ReviewKpis, prev: ReviewKpis) {
  return {
    blocksOverThreshold: cur.blocksOverThreshold - prev.blocksOverThreshold,
    pestBlockExceedances: cur.pestBlockExceedances - prev.pestBlockExceedances,
    approaching: cur.approaching - prev.approaching,
    avgCoveragePct: cur.avgCoveragePct - prev.avgCoveragePct,
  };
}
